import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Plus } from "lucide-react";

export function CrudPage({ title, subtitle, endpoint, columns, fields, testId, refs = {}, canCreate = true, defaultValues = {} }) {
  const { data, loading, reload } = useFetch(endpoint);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm({ ...defaultValues }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const submit = async () => {
    // validate required
    for (const f of fields) {
      if (f.required && (form[f.name] === undefined || form[f.name] === "" || form[f.name] === null)) {
        toast.error(`${f.label} wajib diisi`);
        return;
      }
    }
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number" && payload[f.name] !== undefined && payload[f.name] !== "") payload[f.name] = Number(payload[f.name]); });
    setSaving(true);
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Data diperbarui" : "Data ditambahkan");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}
        action={canCreate && <Button onClick={openNew} data-testid={`${testId}-add-btn`} className="bg-[#9333EA] hover:bg-[#7e22ce]"><Plus className="h-4 w-4 mr-1" /> Tambah</Button>} />
      {loading ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <DataTable testId={testId} columns={columns} data={data || []} onRowClick={openEdit} />
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto scrollbar-thin">
          <SheetHeader>
            <SheetTitle className="font-display">{editing ? "Edit" : "Tambah"} {title}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-6">
            {fields.map((f) => (
              <div key={f.name} className="space-y-1.5">
                <Label className="text-sm">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.name] != null ? String(form[f.name]) : ""} onValueChange={(v) => setField(f.name, v)}>
                    <SelectTrigger data-testid={`${testId}-field-${f.name}`}><SelectValue placeholder={`Pilih ${f.label}`} /></SelectTrigger>
                    <SelectContent>
                      {(f.optionsRef ? (refs[f.optionsRef] || []).map((o) => ({ value: o.id, label: `${o.code ? o.code + " · " : ""}${o.name || o.number}` })) : f.options || []).map((o) => (
                        <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea data-testid={`${testId}-field-${f.name}`} value={form[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} />
                ) : (
                  <Input data-testid={`${testId}-field-${f.name}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.name] ?? ""} onChange={(e) => setField(f.name, e.target.value)} />
                )}
              </div>
            ))}
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid={`${testId}-save-btn`} className="bg-[#9333EA] hover:bg-[#7e22ce]">{saving ? "Menyimpan..." : "Simpan"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
